import { ChevronsDown, ChevronsUp } from 'lucide-react';

import { Checkbox } from 'components/Checkbox';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { cn } from 'utils/shadcn';

import { LoadBuildingAccordionDefaultView } from '../hooks/useLoadBuildingAccordionTabs';

type LoadBuildingAccordionControlsProps = {
  activeTabs: string[];
  // Every accordion value Expand all should open (may include nested stops).
  allTabs: string[];
  /**
   * Top-level section values that drive Expand vs Collapse label.
   * Defaults to `allTabs`. Pass this when nested items (e.g. pickup under route)
   * can remain in `activeTabs` after the parent section is closed.
   */
  sectionTabs?: string[];
  onExpandAll: () => void;
  onCollapseAll: () => void;
  defaultView: LoadBuildingAccordionDefaultView;
  onDefaultViewChange: (view: LoadBuildingAccordionDefaultView) => void;
  className?: string;
};

/**
 * Expand/collapse-all controls styled like load-builder accordion headers
 * (neutral text, chevrons, bottom border) — ENG-7579.
 *
 * Shows one bulk action at a time:
 * - all top-level sections collapsed → Expand all
 * - any top-level section expanded → Collapse all
 */
export default function LoadBuildingAccordionControls({
  activeTabs,
  allTabs: _allTabs,
  sectionTabs,
  onExpandAll,
  onCollapseAll,
  defaultView,
  onDefaultViewChange,
  className,
}: LoadBuildingAccordionControlsProps) {
  const autoExpand = defaultView === 'expanded';
  const tabsForToggle = sectionTabs ?? _allTabs;
  const allCollapsed = !tabsForToggle.some((tab) => activeTabs.includes(tab));

  return (
    <Flex
      align='center'
      justify='center'
      gap='sm'
      wrap='none'
      role='group'
      aria-label='Section expand controls'
      className={cn(
        'mt-2 mb-1 w-full min-w-0 border-b border-neutral-400 px-1 pb-3 pt-1',
        className
      )}
    >
      {allCollapsed ? (
        <button
          type='button'
          onClick={onExpandAll}
          className='flex shrink-0 items-center gap-1 whitespace-nowrap text-neutral-700 hover:underline cursor-pointer'
        >
          <ChevronsDown className='h-3.5 w-3.5 shrink-0' strokeWidth={1.5} />
          <Typography variant='body-xs' className='text-inherit'>
            Expand all
          </Typography>
        </button>
      ) : (
        <button
          type='button'
          onClick={onCollapseAll}
          className='flex shrink-0 items-center gap-1 whitespace-nowrap text-neutral-700 hover:underline cursor-pointer'
        >
          <ChevronsUp className='h-3.5 w-3.5 shrink-0' strokeWidth={1.5} />
          <Typography variant='body-xs' className='text-inherit'>
            Collapse all
          </Typography>
        </button>
      )}
      <Checkbox
        id='load-building-auto-expand'
        checked={autoExpand}
        onCheckedChange={(checked) =>
          onDefaultViewChange(checked === true ? 'expanded' : 'collapsed')
        }
        aria-label='Auto-expand sections'
        label='Auto-expand'
        labelClassName='text-xs leading-none whitespace-nowrap text-neutral-700 cursor-pointer'
        className={cn(
          'h-3.5 w-3.5 shrink-0 border-neutral-400 bg-neutral-50 [&_svg]:h-3 [&_svg]:w-3',
          'data-[state=checked]:border-neutral-500 data-[state=checked]:bg-neutral-50',
          'data-[state=checked]:[&_svg]:!stroke-neutral-700'
        )}
      />
    </Flex>
  );
}
